import React, { useState, useContext } from 'react';
import { Wallet, PiggyBank, TrendingUp } from 'lucide-react';
import TransferModal from './TransferModal';
import { UserContext } from '../../contexts/UserContext';

export type Account = {
  id: 'general' | 'savings';
  name: string;
  balance: number;
  icon: React.ReactNode;
  color: string;
};

export default function AccountOverview() {
  const { userProfile } = useContext(UserContext);
  const [accounts, setAccounts] = useState<Account[]>([
    {
      id: 'general',
      name: 'General Account',
      balance: userProfile?.general ?? 0,
      icon: <Wallet className="w-5 h-5" />,
      color: 'text-blue-500'
    },
    {
      id: 'savings',
      name: 'Savings Account',
      balance: userProfile?.savings ?? 0,
      icon: <PiggyBank className="w-5 h-5" />,
      color: 'text-green-500'
    }
  ]);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const handleTransfer = (fromId: Account['id'], toId: Account['id'], amount: number) => {
    setAccounts(prev => prev.map(account => {
      if (account.id === fromId) {
        return { ...account, balance: account.balance - amount };
      }
      if (account.id === toId) {
        return { ...account, balance: account.balance + amount };
      }
      return account;
    }));
    setShowTransferModal(false);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.map(account => (
          <div
            key={account.id}
            className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={account.color}>
                {account.icon}
              </div>
              <h3 className="font-semibold text-gray-800">{account.name}</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{account.balance} pts</p>
            <p className="text-sm text-gray-500 mt-1">
              {account.id === 'general' ? 'Available to spend' : 'Total saved'}
            </p>
          </div>
        ))}
        {(userProfile?.monthlyEarnings ?? 0) > 0 && (
          <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold text-gray-800">This Month</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {`+${userProfile?.monthlyEarnings}`} pts
            </p>
            <p className="text-sm text-gray-500 mt-1">Net earnings</p>
          </div>
        )}
      </div>
      
      {showTransferModal && (
        <TransferModal
          accounts={accounts}
          onTransfer={handleTransfer}
          onClose={() => setShowTransferModal(false)}
        />
      )}
    </>
  );
}